export const MAX_INPUT_BYTES = 1_048_576

export const OPERATIONS = [
  "format-json",
  "validate-json",
  "json-to-yaml",
  "yaml-to-json",
] as const

export type Operation = (typeof OPERATIONS)[number]
export type InputFormat = "json" | "yaml"
export type OutputFormat = "json" | "yaml" | "none"
export type JsonOutputStyle = "pretty" | "compact"
export type RouteMode = "formatter" | "validator" | "yaml-json"
export type SessionStatus =
  | "empty"
  | "editing"
  | "processing"
  | "success"
  | "warning"
  | "error"
export type ResponseStatus = "success" | "warning" | "error"
export type DiagnosticSeverity = "error" | "warning"
export type AnalyticsOutcome = "success" | "warning" | "error"

export interface Diagnostic {
  severity: DiagnosticSeverity
  code: string
  message: string
  offset: number | null
  length: number | null
  line: number | null
  column: number | null
}

export interface ConversionWarning {
  code: string
  message: string
  sourceFormat: InputFormat
  targetFormat: Exclude<OutputFormat, "none">
  offset: number | null
  line: number | null
  column: number | null
}

export interface ProcessingRequest {
  requestId: string
  operation: Operation | string
  input: string
  inputBytes: number
  outputStyle?: JsonOutputStyle
}

export interface ProcessingResponse {
  requestId: string
  status: ResponseStatus
  output: string | null
  diagnostics: Diagnostic[]
  warnings: ConversionWarning[]
}

export interface WorkbenchSession {
  routeMode: RouteMode
  operation: Operation
  inputFormat: InputFormat
  outputFormat: OutputFormat
  outputStyle: JsonOutputStyle
  inputText: string
  outputText: string | null
  status: SessionStatus
  diagnostics: Diagnostic[]
  warnings: ConversionWarning[]
  isBusy: boolean
}

export type AnalyticsEventName =
  | "job_completed"
  | "job_error"
  | "result_copied"
  | "result_downloaded"
  | "return_visit"

export interface AnalyticsEvent {
  name: AnalyticsEventName
  operation?: Operation
  input_format?: InputFormat
  output_format?: Exclude<OutputFormat, "none">
  outcome?: AnalyticsOutcome
}

export interface WorkbenchModeConfig {
  key: RouteMode
  label: string
  operation: Operation
  inputFormat: InputFormat
  outputFormat: OutputFormat
}

export function isOperation(value: string): value is Operation {
  return (OPERATIONS as readonly string[]).includes(value)
}

export function createEmptySession(config: WorkbenchModeConfig): WorkbenchSession {
  return {
    routeMode: config.key,
    operation: config.operation,
    inputFormat: config.inputFormat,
    outputFormat: config.outputFormat,
    outputStyle: "pretty",
    inputText: "",
    outputText: null,
    status: "empty",
    diagnostics: [],
    warnings: [],
    isBusy: false,
  }
}
